import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { useAnimalFilters } from '../../../../hooks/animalFilters';

import BackHeader from '../../../../components/BackHeader';
import SearchInput from '../../../../components/Form/SearchInput';
import AnimalItem from '../../../../components/Animal/Item';
import AnimalFiltersButton from '../../../../components/AnimalFiltersButton';
import AnimalFiltersModal from '../../../../components/AnimalFiltersModal';
import { H2 } from '../../../../components/Generic';
import {
  Container,
  Content,
  NotFoundContainer,
  SelectedAnimalsContainer,
  SelectAllButton,
} from './styles';
import { IconInfoCircle } from '@tabler/icons';
import LoadingComponent from './components/LoadingComponent';

const SelectAnimals: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [animals, setAnimals] = useState<any>([]);
  const [query, setQuery] = useState('');

  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalFound, setTotalFound] = useState(0);

  const [selectedAnimals, setSelectedAnimals] = useState<any>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const { id_property }: any = useParams();
  const { addToast } = useToast();
  const history = useHistory();

  const { animalFilters } = useAnimalFilters();

  const [scrollRadio, setScrollRadio] = useState(null);
  const scrollObserve = useRef<any>();

  useEffect(() => {
    intersectionObserver.observe(scrollObserve.current);

    return () => {
      intersectionObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    handleSearch();
  }, [animalFilters]);

  useEffect(() => {
    if (selectedAnimals.length > 0) {
      const containsAll = animals.every((animal: any) => {
        return selectedAnimals.some((a: any) => a.id === animal.id);
      });

      setIsAllSelected(containsAll);
    }
  }, [selectedAnimals]);

  useEffect(() => {
    if (hasMore && scrollRadio && !loading) {
      loadMore();
    }
  }, [scrollRadio]);

  const intersectionObserver = new IntersectionObserver((entries: any) => {
    const radio = entries[0].intersectionRatio;
    setScrollRadio(radio);
  });

  const handleSearch = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/pesquisas/animais`, {
        headers: {
          propriedade_id: id_property,
        },
        params: {
          ...animalFilters,
          pertencentes: !!animalFilters.pertencentes,
          key: query,
          limite: 10,
          cursor_id: '',
        },
      });

      setAnimals(response.data.animais);

      const total = response.data.animais.length;
      console.log(total);

      setHasMore(total < response.data.total);

      setTotalFound(response.data.total);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);

      const response = await api.get(`/pesquisas/animais`, {
        headers: {
          propriedade_id: id_property,
        },
        params: {
          ...animalFilters,
          pertencentes: !!animalFilters.pertencentes,
          key: query,
          limite: 10,
          cursor_id: animals[animals.length - 1]?.id || '',
        },
      });
      setAnimals(animals.concat(response.data.animais));

      const total = animals.concat(response.data.animais).length;

      console.log(total);

      setHasMore(total < response.data.total);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleAnimal = (animal: any) => {
    if (selectedAnimals.some((e: any) => e.id === animal.id)) {
      setSelectedAnimals((prev: any) =>
        prev.filter((a: any) => a.id !== animal.id),
      );
    } else {
      setSelectedAnimals((prev: any) => prev.concat(animal));
    }
  };

  const addAllAnimalsFromQuery = async () => {
    try {
      const response = await api.get(`/pesquisas/animais`, {
        headers: {
          propriedade_id: id_property,
        },
        params: {
          ...animalFilters,
          pertencentes: !!animalFilters.pertencentes,
          key: query,
          limite: totalFound,
          cursor_id: '',
        },
      });

      const { animais } = response.data;

      animais.forEach((animal: any) => {
        if (selectedAnimals.some((e: any) => e.id === animal.id)) {
          // animal is already selected
        } else {
          setSelectedAnimals((prev: any) => prev.concat(animal));
        }
      });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Container>
      <BackHeader title="Nova transferência" />

      <Content>
        <H2>Selecione os animais</H2>

        <SearchInput
          placeholder="Digite o número, nome ou botom"
          onChange={(e: any) => setQuery(e.target.value)}
          onSubmit={handleSearch}
          style={{ marginBottom: 20 }}
        />

        <AnimalFiltersButton propertyId={id_property} />

        <hr />

        {loading ? (
          <LoadingComponent />
        ) : animals?.length > 0 ? (
          <>
            {totalFound > 0 && (
              <>
                <div className="search-info">
                  <p>
                    <strong>{totalFound}</strong>{' '}
                    {totalFound > 1
                      ? 'animais encontrados.'
                      : 'animal encontrado.'}
                  </p>
                </div>

                <SelectAllButton onClick={addAllAnimalsFromQuery}>
                  <div id="dot">{isAllSelected && <span />}</div> Selecionar
                  todos
                </SelectAllButton>
              </>
            )}

            {animals.map((animal: any) => (
              <AnimalItem
                key={animal.id}
                imageUrl={
                  animal.imagens?.length > 0 ? animal.imagens[0]?.image_url : ''
                }
                name={animal.nome}
                birth={animal.data_de_nascimento}
                number={animal.numero_de_identificacao}
                gender={animal.sexo}
                state={animal.estado}
                isPrivate={animal.privado}
                belongs={animal.pertencente}
                customStyle={{ marginTop: 20, cursor: 'pointer' }}
                onClick={() => toggleAnimal(animal)}
                selectable
                selected={selectedAnimals.some((e: any) => e.id === animal.id)}
              />
            ))}
          </>
        ) : (
          <NotFoundContainer>
            <IconInfoCircle size={20} />

            <strong>Nenhum animal encontrado.</strong>
          </NotFoundContainer>
        )}

        <div ref={scrollObserve} />

        {loadingMore && <LoadingComponent />}

        <div style={{ height: 80 }} />
      </Content>

      <SelectedAnimalsContainer>
        <div>{selectedAnimals.length} animais selecionados</div>
        <button
          onClick={() => {
            if (selectedAnimals.length > 0) {
              history.push({
                pathname: `/propriedade/${id_property}/transferencias/enviar`,
                state: { selectedAnimals },
              });
            } else {
              alert('Por favor, selecione os animais para serem transferidos.');
            }
          }}
        >
          Continuar
        </button>
      </SelectedAnimalsContainer>
    </Container>
  );
};

export default SelectAnimals;
